import { useEffect, useState, useRef } from 'react';
import css from './SoftAlert.module.css';
import ErrorIcon from '@/styles/Icons/ErrorIcon';
import InformationIcon from '@/styles/Icons/InformationIcon';
import TickIcon from '@/styles/Icons/TickIcon';
import { CloseDeleteIcon as CloseIcon } from '@/styles/Icons/CloseDelete';
import { classNames } from '@/utils/helpers/classNames';
import { SoftAlertType } from './types';

const FADE_DURATION_MS = 200;

interface SoftAlertProps {
  content: React.ReactNode;
  type: SoftAlertType;
  hideTimeout?: number;
  onClose: () => void;
}

export default function SoftAlert({ content, type, hideTimeout, onClose }: SoftAlertProps) {
  const [isVisible, setIsVisible] = useState(true);
  const autoHideTimeoutRef = useRef<NodeJS.Timeout>();
  const fadeTimeoutRef = useRef<NodeJS.Timeout>();

  let Icon: React.ComponentType;
  switch (type) {
    case SoftAlertType.inProgress:
      Icon = InformationIcon;
      break;
    case SoftAlertType.error:
      Icon = ErrorIcon;
      break;
    case SoftAlertType.success:
      Icon = TickIcon;
      break;
    default:
      Icon = InformationIcon;
  }

  useEffect(() => {
    if (hideTimeout !== undefined) {
      autoHideTimeoutRef.current = setTimeout(() => {
        setIsVisible(false);
        fadeTimeoutRef.current = setTimeout(() => onClose(), FADE_DURATION_MS);
      }, hideTimeout);
    }

    return () => {
      if (autoHideTimeoutRef.current) clearTimeout(autoHideTimeoutRef.current);
      if (fadeTimeoutRef.current) clearTimeout(fadeTimeoutRef.current);
    };
  }, [hideTimeout, onClose]);

  const wrapperClassName = classNames({
    [css.wrapper]: true,
    [css.wrapperInProgress]: type === SoftAlertType.inProgress,
    [css.wrapperError]: type === SoftAlertType.error,
    [css.wrapperSuccess]: type === SoftAlertType.success
  });

  const handleClose = () => {
    // Clear auto-hide timeout to prevent double execution
    if (autoHideTimeoutRef.current) {
      clearTimeout(autoHideTimeoutRef.current);
      autoHideTimeoutRef.current = undefined;
    }

    setIsVisible(false);
    fadeTimeoutRef.current = setTimeout(() => onClose(), FADE_DURATION_MS);
  };

  return (
    <div className={classNames({ [css.transparent]: !isVisible })}>
      <div className={wrapperClassName}>
        <div className={type === SoftAlertType.success ? css.successIconContainer : css.iconContainer}>
          <Icon />
        </div>
        <div className={css.label}>{content}</div>
        <button className={css.closeButton} onClick={handleClose} aria-label="Close alert">
          <CloseIcon className={null} />
        </button>
      </div>
    </div>
  );
}
